/**
 * Scans taken on the floor, on their way to the server.
 *
 * A store room is where the signal dies, and it is also where the scanning
 * happens. So the scanner never waits for the network to tell someone their scan
 * worked: the scan is written here first, the screen confirms immediately, and
 * this module gets it to the server whenever there is a connection.
 *
 * Separate from the sync outbox by design - see `QueuedScan` in db/schema.ts for
 * why an append-only fact and a conflicting edit do not belong in one queue.
 *
 * The one thing this must never do is hide the difference between "recorded" and
 * "everyone can see it". A scan sitting in this queue is real to the person who
 * took it and invisible to everybody else, and the UI says exactly that until it
 * has been sent.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import type { ScanEventInput } from '@mouldtrack/shared';
import { db, type QueuedScan } from '../../db/schema.js';
import { isNetworkError, postScanBatch } from '../../api/client.js';
import { refreshCounters } from '../../sync/outbox.js';

/** Server cap on one batch (`scanBatchSchema`). Sending more is a 400. */
const BATCH_SIZE = 100;

/** Stop retrying after this many failures and let the person decide. */
const MAX_ATTEMPTS = 8;

export type QueuedScanInput = Omit<QueuedScan, 'seq' | 'status' | 'attempts' | 'lastError'>;

/** Write a scan to the device. Durable the moment this resolves. */
export async function enqueueScan(input: QueuedScanInput): Promise<void> {
  await db.scanQueue.add({ ...input, status: 'queued', attempts: 0 });
  // The shell's "N changes waiting" and both sign-out warnings read these
  // counters, and an unsent scan has to be in them from the moment it exists.
  await refreshCounters();
}

function toPayload(row: QueuedScan): ScanEventInput {
  return {
    clientScanId: row.clientScanId,
    assetCode: row.assetCode,
    checkpointCode: row.checkpointCode,
    scannedAt: row.scannedAt,
    ...(row.remarks ? { remarks: row.remarks } : {}),
    ...(row.expectedBackBy ? { expectedBackBy: row.expectedBackBy } : {}),
  };
}

export interface FlushResult {
  sent: number;
  failed: number;
  /** True when nothing could be attempted because the device is offline. */
  offline: boolean;
}

let inFlight: Promise<FlushResult> | null = null;

/**
 * Send everything that is waiting.
 *
 * Single-flight: a reconnect, a tab regaining focus and a fresh scan can all
 * fire this within the same second, and three overlapping runs would send the
 * same rows three times. The server would dedupe them on `clientScanId`, but
 * "correct because the other end cleans up after us" is not a design.
 */
export function flushScanQueue(): Promise<FlushResult> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const empty: FlushResult = { sent: 0, failed: 0, offline: false };
    if (!navigator.onLine) {
      const waiting = await db.scanQueue.where('status').anyOf('queued', 'sending').count();
      return { ...empty, offline: waiting > 0 };
    }

    let sent = 0;
    let failed = 0;

    for (;;) {
      // Oldest first. The server re-sorts by `scannedAt` before applying, so
      // status derivation lands on the true latest movement even if a device
      // was offline across several of them.
      const batch = (await db.scanQueue.where('status').anyOf('queued', 'sending').sortBy('seq'))
        .slice(0, BATCH_SIZE);
      if (batch.length === 0) break;

      try {
        const { results } = await postScanBatch(batch.map(toPayload));
        const verdict = new Map(results.map((r) => [r.clientScanId, r]));

        for (const row of batch) {
          if (row.seq === undefined) continue;
          const result = verdict.get(row.clientScanId);

          // No verdict means the server never saw this one. Leave it queued.
          if (!result) continue;

          // `deduped` is a success: the row reached the server on an earlier
          // attempt whose response we lost. That is exactly what the
          // idempotency key is for, and it must not read as an error.
          if (result.status === 'created' || result.status === 'deduped') {
            await db.scanQueue.delete(row.seq);
            sent += 1;
            continue;
          }

          // Rejected: an unknown code, a scrapped mould, a checkpoint this
          // account may not scan at. Retrying cannot fix any of those, so it
          // stops here and waits to be shown to a person.
          failed += 1;
          await db.scanQueue.update(row.seq, {
            status: 'failed',
            attempts: row.attempts + 1,
            lastError: result.error?.message ?? result.error?.code ?? 'Rejected by the server',
          });
        }
      } catch (error) {
        // The whole request failed in transit. Nothing was recorded, so every
        // row stays queued - losing a scan to a dropped connection is the one
        // outcome this queue exists to prevent.
        const message = isNetworkError(error)
          ? 'No connection'
          : error instanceof Error
            ? error.message
            : 'Send failed';
        for (const row of batch) {
          if (row.seq === undefined) continue;
          const attempts = row.attempts + 1;
          await db.scanQueue.update(row.seq, {
            attempts,
            status: attempts >= MAX_ATTEMPTS ? 'failed' : 'queued',
            lastError: message,
          });
        }
        return { sent, failed, offline: isNetworkError(error) };
      }

      if (batch.length < BATCH_SIZE) break;
    }

    return { sent, failed, offline: false };
  })()
    .finally(async () => {
      inFlight = null;
      await refreshCounters();
    });

  return inFlight;
}

/** Re-arm a scan that was rejected, after whatever blocked it has been fixed. */
export async function retryScan(seq: number): Promise<void> {
  await db.scanQueue.update(seq, { status: 'queued', attempts: 0, lastError: undefined });
  await refreshCounters();
}

/**
 * Throw a scan away.
 *
 * Only reachable behind a confirmation, and only for scans the server has
 * refused outright. Silently dropping a movement record would leave a tool
 * standing somewhere the system says it is not.
 */
export async function discardScan(seq: number): Promise<void> {
  await db.scanQueue.delete(seq);
  await refreshCounters();
}

/** Everything still on this device, newest first. */
export function useScanQueue(): QueuedScan[] | undefined {
  return useLiveQuery(() => db.scanQueue.orderBy('seq').reverse().toArray(), []);
}

/** How many scans nobody else can see yet. Drives the warning on the scanner. */
export function useUnsentScanCount(): number {
  return (
    useLiveQuery(() => db.scanQueue.where('status').anyOf('queued', 'sending').count(), [], 0) ?? 0
  );
}
